<template>
  <div>
    <div v-if="businessVerticals.length > 0">
      <Table :columns="columns" :data="businessVerticals">
        <template #merchantCommission="{ row }">
          <FieldSpan :style-to-add="'bg-green-200'" :value="formatPercentage(row.storeFee)" />
        </template>
        <template #storeAdditionalFee="{ row }">
          <FieldSpan
            v-if="row.storeAdditionnalFee"
            :style-to-add="'bg-green-200'"
            :value="formatPercentage(row.storeAdditionnalFee)"
          />
          <p class="font-bold" v-else>—</p>
        </template>
        <template #thirdPartyActor="{ row }">
          <div v-if="row.thirdPartyActorFees && row.thirdPartyActorFees.length > 0">
            <span v-for="(fee, index) in row.thirdPartyActorFees" :key="fee.thirdPartyActorId">
              {{ fee.thirdPartyActorName }} ({{ formatPercentage(fee.fee) }})
              <span v-if="index < row.thirdPartyActorFees.length - 1">,</span>
            </span>
          </div>
          <p class="font-bold" v-else>—</p>
        </template>
        <template #marketingPage="{ row }">
          <a :href="row.marketingPage" alt="marketing page" target="_blank" class="text-blue-400 underline">
            {{ t("Marketing page link") }}
          </a>
        </template>
        <template #actions="{ row }">
          <PencilAltIcon class="h-5 cursor-pointer inline-block" @click="update(row)" />
        </template>
      </Table>
    </div>
    <div v-else class="text-center text-sm text-gray-500 py-4">
      {{ t("no_business_vertical") }}
    </div>

    <div class="flex justify-end mt-4">
      <SecondaryButton @click="newBusinessVertical">{{ t("add") }}</SecondaryButton>
    </div>

    <Modal :open="displayBusinessVerticalModalForm" @close="displayBusinessVerticalModalForm = false">
      <BusinessVerticalForm
        :store="store"
        :businessVertical="selectedBusinessVertical"
        @success="businessVerticalFormSuccess"
      />
    </Modal>
  </div>
</template>

<script>
import { PencilAltIcon } from "@heroicons/vue/outline";
import { Modal, SecondaryButton, Table, FieldSpan, toCamelCase, formatPercentage } from "@estaly/ui";
import BusinessVerticalForm from "@/components/store/BusinessVerticalForm.vue";
import { mapActions } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  components: {
    PencilAltIcon,
    FieldSpan,
    SecondaryButton,
    Modal,
    BusinessVerticalForm,
    Table,
  },
  props: ["store"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data: () => {
    return {
      collapsableOpen: false,
      businessVerticals: [],
      displayBusinessVerticalModalForm: false,
      selectedBusinessVertical: null,
    };
  },
  computed: {
    columns() {
      return [
        { label: this.t("Vertical"), field: "category" },
        { label: this.t("Merchant commission"), field: "storeFee", slot: "merchantCommission" },
        { label: this.t("Store additional fee commission"), field: "storeAdditionnalFee", slot: "storeAdditionalFee" },
        { label: this.t("Third party actor"), field: "thirdPartyActorFees", slot: "thirdPartyActor" },
        { label: this.t("Marketing page"), field: "marketingPage", slot: "marketingPage" },
        { label: this.t("Insurer"), field: "insurerName" },
        { label: this.t("sale_form_type"), value: "translatedSaleFormType", field: "translatedSaleFormType" },
        { label: "", field: "actions", slot: "actions" },
      ];
    },
  },
  methods: {
    ...mapActions("businessVerticals", ["fetch"]),
    formatPercentage,
    async fetchBusinessVerticals() {
      const response = await this.fetch({
        storeId: [this.store.id],
        enabled: true,
        includes: ["third_party_actor_fees"],
      });
      if (!response?.success) return;

      this.businessVerticals = toCamelCase(response.data)?.records || [];
    },
    async businessVerticalFormSuccess() {
      this.displayBusinessVerticalModalForm = false;
      await this.fetchBusinessVerticals();
    },
    newBusinessVertical() {
      this.collapsableOpen = true;
      this.selectedBusinessVertical = null;
      this.displayBusinessVerticalModalForm = true;
    },
    update(businessVertical) {
      this.collapsableOpen = true;
      this.selectedBusinessVertical = businessVertical;
      this.displayBusinessVerticalModalForm = true;
    },
  },
  async mounted() {
    await this.fetchBusinessVerticals();
  },
};
</script>

<i18n src="./StoreBusinessVerticals.i18n.json"></i18n>
